import { Phone, BadgeCheck, PhoneIncoming, PhoneForwarded, PhoneOutgoing, PhoneCall, CalendarClock, AudioLines, Megaphone, Sparkles, HardDrive, ListTree, Bot, BellRing } from "lucide-react";
import { PHONE_TAB_PERM as P } from "../perms";
import type { SubsectionTab } from "../subsection";
import { TelephonyIntegrationCard } from "./TelephonyIntegrationCard";
import { CallerIdCard } from "./CallerIdCard";
import { InboundCard } from "./InboundCard";
import { HoursCard } from "./HoursCard";
import { MenuCard } from "./MenuCard";
import { DivertCard } from "./DivertCard";
import { BusyModeCard } from "./BusyModeCard";
import { TranscriptionCard } from "./TranscriptionCard";
import { NoticeCard } from "./NoticeCard";
import { PilotCard } from "./PilotCard";
import { AiAgentCard } from "./aiAgent";
import { AiOutboundCard } from "./aiOutbound";
import { RecordingStorageCard } from "./RecordingStorageCard";
import { AppPushCard } from "./AppPushCard";

/**
 * Order is the order somebody sets a phone line up in.
 *
 * The Twilio connection comes first because nothing under it does anything
 * without those credentials. Caller ids follow, then inbound - the same
 * question read backwards: which number we show going out, and what happens on
 * it coming in. Working hours sits directly under inbound because it is the
 * second half of that answer, and it changes nothing until the tab above it is
 * switched on. Forwarding is the third question in that run - "and what if
 * nobody is here at all" - and follows working hours because one of its two
 * rules fires on that schedule.
 * "During a call" closes that run with the one question that belongs to a
 * person rather than to the line: what a new call does while I am already on
 * one. It is the only tab here an operator without the line's settings right
 * sees, and for them it is the whole menu.
 * Transcription comes next because it is the first tab about what was SAID
 * rather than about who rang whom. The spoken notice goes between it and the
 * live co-pilot, directly above the tab it gates: it announces both AI passes,
 * and an operator wondering why the co-pilot panel is off should walk into the
 * answer on the way there.
 * AI answering follows the co-pilot: both put a model on a live call, and the
 * co-pilot is the one an operator already knows. It sits this far down on
 * purpose - it cannot answer anybody without the switch on Inbound and the two
 * keys on Transcription, and its notes point back up this menu to both.
 * AI calling out follows answering directly: it borrows that tab's voice, model
 * and knowledge, so the answering tab is set up first and this one points to it.
 * Recording storage is last: it is maintenance on what the stack above has
 * already produced.
 */
// The permissions are referenced, not typed in. They live in `../perms` beside
// every other Settings gate, because `crm/nav.ts` reads the same list to decide
// whether Settings appears at all and cannot import this file - that would pull
// nine panels into the sidebar's module graph.
export const PHONE_TABS: SubsectionTab[] = [
  { path: "telephony", key: "telephony", icon: Phone, perm: P.telephony, panel: TelephonyIntegrationCard },
  { path: "caller-id", key: "callerId", icon: BadgeCheck, perm: P["caller-id"], panel: CallerIdCard },
  { path: "inbound", key: "inbound", icon: PhoneIncoming, perm: P.inbound, panel: InboundCard },
  { path: "menu", key: "menu", icon: ListTree, perm: P.menu, panel: MenuCard },
  { path: "hours", key: "hours", icon: CalendarClock, perm: P.hours, panel: HoursCard },
  { path: "forwarding", key: "forwarding", icon: PhoneForwarded, perm: P.forwarding, panel: DivertCard },
  { path: "busy", key: "busy", icon: PhoneCall, perm: P.busy, panel: BusyModeCard },
  // The phone app's own notices, beside the person's "during a call" choice: both are
  // about the phone in the pocket rather than the line.
  { path: "app-push", key: "appPush", icon: BellRing, perm: P["app-push"], panel: AppPushCard },
  { path: "transcription", key: "transcription", icon: AudioLines, perm: P.transcription, panel: TranscriptionCard },
  { path: "notice", key: "notice", icon: Megaphone, perm: P.notice, panel: NoticeCard },
  { path: "pilot", key: "pilot", icon: Sparkles, perm: P.pilot, panel: PilotCard },
  { path: "ai-agent", key: "aiAgent", icon: Bot, perm: P["ai-agent"], panel: AiAgentCard },
  { path: "ai-outbound", key: "aiOutbound", icon: PhoneOutgoing, perm: P["ai-outbound"], panel: AiOutboundCard },
  {
    path: "recording-storage",
    key: "recordingStorage",
    icon: HardDrive,
    perm: P["recording-storage"],
    panel: RecordingStorageCard,
  },
];
